<template>
    <div v-show="dialogVisible" :class="'fixed z-50 inset-0 ' + additionalBodyClasses">
        <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 transition-opacity">
                <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <!-- This element is to trick the browser into centering the modal contents. -->
            <span class="hidden sm:inline-block sm:align-middle sm:h-screen"></span>&#8203;

            <div
                class="max-w-3xl inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:p-6"
                :class="fullWidth ? 'sm:w-full' : 'sm:w-6/12'"
                role="dialog" aria-modal="true" aria-labelledby="modal-headline" v-show="!isLoading">
                <div>
                    <div class="mt-3">
                        <div class="grid grid-cols-5">
                            <h3 class="col-span-5 lg:col-span-3 text-lg leading-6 font-bold text-gray-900" id="modal-headline">
                                Select Existing Media To Attach
                            </h3>
                            <div class="col-span-5 lg:col-span-2 mt-4 lg:mt-0">
                                <simple-dropdown-with-multi-select :options="modelOptions"
                                                                   :pre-selected-options="appliedFilters"
                                                                   @selected-options-changed="(options)=>this.appliedFilters = options">
                                </simple-dropdown-with-multi-select>
                            </div>
                        </div>

                        <div v-if="isLoading || imagesLoading" class="flex align-items-center border-2 border-grey-300 rounded-lg mt-6" style="height: 50vh">
                            <div class="loader"></div>
                        </div>

                        <div v-show="!(isLoading || imagesLoading)">
                            <div v-if="allMedia.length <= 0"
                                 class="flex justify-center align-items-center mt-6 border-2 border-grey-300 rounded-lg font-semibold" style="height: 50vh">
                                {{ noMediaMessage ? noMediaMessage : "No Media Found" }}
                            </div>
                            <div v-else class="mt-6 border-2 border-gray-300 rounded-lg p-4 overflow-y-auto" style="height:50vh">
                                <div v-for="(mediaItem,index) in allMedia" @click="selectMedia(mediaItem)" class="cursor-pointer my-4 rounded p-2"
                                     :class="{'bg-gray-50' : index % 2 !== 0, 'bg-indigo-50' : selectedMedia.includes(mediaItem)}">
                                    <div class="grid grid-cols-12 items-center relative">
                                        <div class="col-span-2">
                                            <div class="relative flex item-center">
                                                <img class="rounded-lg w-16"
                                                     v-if="mediaItem.type === 'image'"
                                                     :src="mediaItem.url"
                                                     :alt="mediaItem.url"
                                                     @load="setMediaAsLoaded(mediaItem)">
                                                <div @click="selectMedia(mediaItem)" v-else class="cursor-pointer max-w-xs max-h-xs">
                                                    <icon-audio-solid v-if="mediaItem.type === 'audio'" class="w-16 h-16"></icon-audio-solid>
                                                    <icon-document-solid v-if="mediaItem.type === 'document'" class="w-16 h-16"></icon-document-solid>
                                                    <icon-video-solid v-if="mediaItem.type === 'video'" class="w-16 h-16"></icon-video-solid>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="col-span-8 text-left">
                                            <div class="font-bold">{{ mediaItem.name }}</div>
                                            <div v-if="mediaItem.created_at" class="text-xs text-blue-500">
                                                Added on: {{ mediaItem.created_at | datetime }} &middot; {{ mediaItem.size }}
                                            </div>
                                            <div v-if="mediaItem.related_model" class="text-xs text-gray-500">
                                                Source: {{ mediaItem.related_model | titlecase }}
                                            </div>
                                        </div>
                                        <div class="col-span-2 flex justify-center items-center">
                                            <div v-if="selectedMedia.includes(mediaItem)">
                                                <icon-check class="w-8 h-8 text-blue-500"></icon-check>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-1 sm:gap-3 sm:grid-flow-row-dense">
                    <span class="flex w-full rounded-md shadow-sm sm:col-start-2" v-if="selectedMedia && selectedMedia.length > 0">
                        <button @click.prevent="attachSelectedMedia" type="button"
                                class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-indigo-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:ring focus:ring-indigo-400 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                            {{ submitButtonLabel }}
                        </button>
                    </span>
                    <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:col-start-1"
                          :class="selectedMedia && selectedMedia.length === 0 ? 'col-span-2' : ''">
                        <button :disabled="sending == true" @click.prevent="closeModal()" type="button"
                                class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm  transition ease-in-out duration-150 sm:text-sm sm:leading-5">
                            Cancel
                        </button>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "multi-upload-component",
    props: [
        "show", "mediaUrl", "filterOptions", "submitLabel",
        "noMediaMessage", "sending", "messengerChannelId", "fullWidth",
        "additionalBodyClasses", "preAppliedFilter", "modelType"
    ],

    data () {
        return {
            dialogVisible: this.show,
            allMedia: [],
            selectedMedia: [],
            isLoading: true,
            appliedFilters: [],
        };
    },

    watch: {
        show (show) {
            if (show) {
                this.selectedMedia = [];
                if (this.preAppliedFilter) {
                    this.appliedFilters.push(this.preAppliedFilter);
                }
                this.getAssociatedMedia();
                this.setBodyOverflow("hidden");
            } else {
                this.setBodyOverflow("auto");
            }

            this.dialogVisible = show;
        },

        appliedFilters () {
            this.selectedMedia = [];
            this.getAssociatedMedia();
        },
    },

    mounted () {
        this.getAssociatedMedia();
    },

    computed: {
        submitButtonLabel () {
            //Handle if there is a submit label
            if (this.submitLabel != null) {
                if (this.sending) {
                    return this.submitLabel + "ing...";
                } else {
                    return this.submitLabel + " " + this.selectedMedia.length + " item" + (this.selectedMedia.length !== 1 ? "s" : "");
                }
            } else {
                //handle default behaviour
                if (this.sending) {
                    return "Sending...";
                } else {
                    return "Send " + this.selectedMedia.length + " item" + (this.selectedMedia.length !== 1 ? "s" : "");
                }
            }
        },

        modelOptions () {
            if (this.filterOptions) {
                return this.filterOptions;
            }

            switch (_.toLower(this.modelType)) {
                case "app\\ticket":
                    return ["ticket", "issue", "email"];
                case "app\\inspection":
                    return ["inspection", "checklist", "email"];
                default:
                    return ["email"];
            }
        },

        imagesLoading () {
            return this.allMedia.filter((media) => media.loaded === false).length > 0;
        }
    },

    methods: {
        setLoadingState (state) {
            if (state === false) {
                let self = this;
                setTimeout(() => self.isLoading = state, 250);
                return;
            }

            this.isLoading = state;
        },

        getAssociatedMedia () {
            this.setLoadingState(true);
            axios.get(this.mediaUrl, {
                params: {
                    messenger_channel_id: this.messengerChannelId,
                    model_types_filter: this.appliedFilters
                }
            })
            .then((response) => {
                this.setMediaFromResponse(response);
                this.setLoadingState(false);
            })
            .catch((error) => {
                toastr.error("Failed to get media! " + error);
                this.setLoadingState(false);
            });
        },

        setMediaFromResponse (response) {
            this.allMedia = response.data;

            if (!this.allMedia) {
                return;
            }

            this.allMedia = this.allMedia.map(function (media) {
                media.loaded = media.type !== "image";
                return media;
            });

            // Filter out any duplicates that were created on the email & documents
            // by checking the name and size of the file
            this.allMedia = _.uniqBy(this.allMedia, function (media) {
                return media.size + "-" + media.name;
            });
        },

        setMediaAsLoaded (media) {
            let index = this.allMedia.indexOf(media);

            if (index <= -1) {
                return;
            }

            media.loaded = true;
            Vue.set(this.allMedia, index, media);  //works fine
        },

        closeModal () {
            this.$emit("close-modal");
            this.selectedMedia = [];
            this.allMedia = [];
        },

        selectMedia (mediaItem) {
            if (this.sending) {
                return;
            }

            if (this.selectedMedia.includes(mediaItem)) {
                let index = this.selectedMedia.indexOf(mediaItem);

                if (index > -1) {
                    this.selectedMedia.splice(index, 1);
                } else {
                    toastr.error("Error unselecting Media!");
                }

            } else {
                this.selectedMedia.push(mediaItem);
            }
        },

        attachSelectedMedia () {
            this.$emit("media-selected", this.selectedMedia);
        },

        clearMediaSelection () {
            this.selectedMedia = [];
        },

        setBodyOverflow (overflow = "auto") {
            let elementArray = document.getElementsByTagName("html");

            if (!elementArray) {
                return;
            }

            let htmlTag = elementArray[0];
            if (!htmlTag) {
                return;
            }

            htmlTag.style.overflow = overflow;
        }
    }
};
</script>

<style scoped>
.loader {
    border: 8px solid #f3f3f3; /* Light grey */
    border-top: 8px solid #3498db; /* Blue */
    border-radius: 50%;
    width: 60px;
    height: 60px;
    margin: auto;
    animation: spin 2s linear infinite;
}
</style>
